import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useHttp from '../../../hooks/use-http';
import Spinner from '../../UI/Modal/Spinner';
import Zaglavlje from './components/Zaglavlje';
import ProjectContent from './components/ProjectContent';
import { TabContext } from '../../../Context/TabContext';
import { urls } from './api/urls';
import { normalizeProgresResponse } from './data/mapProgresData';
import { mapDokumentiToUpdates } from './data/mapDokumentiToUpdates';
import { buildUpdatePayload } from './data/buildUpdatePayload';
import { handleErrorMessage } from '../../../../shared/api/handleErrorMessage';
import toast from 'react-hot-toast';

const INITIAL_VALUES = {
  idProjekta: null,
  nazivProjekta: '',
  idPp: null,
  prikazPodatakaKlijentu: false,
};

const ProgresProjekta = () => {
  const { t } = useTranslation();
  const { getData, isLoading } = useHttp('');
  const [filterValues, setFilterValues] = useState(INITIAL_VALUES);
  const [progresData, setProgresData] = useState(null);
  const [updates, setUpdates] = useState([]);
  const { tabsData, activeComponent, onTabChange } = useContext(TabContext);
  const dataLocal = tabsData.find((item) => item.type === activeComponent);
  const localFormValues = dataLocal?.filterValues ?? {};
  const localProgresData = dataLocal?.localProgresData ?? null;
  const localUpdates = dataLocal?.localUpdates ?? null;

  useEffect(() => {
    const tab = {
      type: 1083,
      title: 'Progres projekta',
      icon: 'fas fa-tasks',
      filterValues,
      localProgresData: progresData,
      localUpdates: updates,
    };
    onTabChange(tab);
  }, [filterValues, progresData, updates]);

  useEffect(() => {
    if (Object.keys(localFormValues).length > 0) {
      setFilterValues({ ...INITIAL_VALUES, ...localFormValues });
    }
    if (localProgresData) {
      setProgresData(localProgresData);
    }
    if (localUpdates) {
      setUpdates(localUpdates);
    }
  }, []);

  const fetchProgresData = async (idProjekta) => {
    const response = await getData({
      url: urls.GET_PROGRES,
      method: 'POST',
      data: { id: idProjekta },
    });

    return normalizeProgresResponse(response);
  };

  const fetchDokumenti = async (idMasine) => {
    if (!idMasine) return [];

    const response = await getData({
      url: urls.GET_DOKUMENTI,
      method: 'POST',
      data: { idMasine: idMasine },
    });

    if (response?.isAxiosError) return [];
    return Array.isArray(response) ? response : [];
  };

  const handlePrikazi = async () => {
    if (!filterValues.idProjekta) {
      toast.error(t('projectProgress.selectProject'));
      return;
    }

    const normalized = await fetchProgresData(filterValues.idProjekta);

    if (normalized) {
      setProgresData(normalized);
    } else {
      setProgresData(null);
    }

    const docs = await fetchDokumenti(filterValues.idProjekta);
    setUpdates(mapDokumentiToUpdates(docs));
  };

  const refreshUpdates = async () => {
    const idMasine =
      filterValues.idProjekta ??
      progresData?.idMasine ??
      progresData?.IdMasine ??
      null;

    const docs = await fetchDokumenti(idMasine);
    setUpdates(mapDokumentiToUpdates(docs));
  };

  const handleSaveProgres = async (overrides) => {
    if (!progresData || !filterValues.idProjekta) return false;

    const payload = buildUpdatePayload(progresData, overrides);
    const response = await getData({
      url: urls.UPDATE_PROGRES,
      method: 'POST',
      data: payload,
    });

    if (response?.isAxiosError) {
      toast.error(handleErrorMessage(response));
      return false;
    }

    const rezultat = Number(response?.rezultat ?? response?.Rezultat ?? 0);
    if (rezultat <= 0) {
      toast.error(
        response?.opis ||
          response?.Opis ||
          t('projectProgress.errorUpdatingData'),
      );
      return false;
    }

    const normalized = await fetchProgresData(filterValues.idProjekta);

    if (normalized) {
      setProgresData(normalized);
      toast.success(t('projectProgress.dataSavedSuccessfully'));
      return true;
    }

    toast.error('Greška prilikom učitavanja podataka.');
    return false;
  };

  return (
    <div style={{ padding: '0 4px', width: '95%' }}>
      {isLoading && <Spinner />}
      <Zaglavlje
        filterValues={filterValues}
        setFilterValues={setFilterValues}
        handlePrikazi={handlePrikazi}
      />
      <ProjectContent
        progresData={progresData}
        updates={updates}
        onSaveProgres={handleSaveProgres}
        onRefreshUpdates={refreshUpdates}
      />
    </div>
  );
};

export default ProgresProjekta;
